import React, { useCallback, useEffect, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import { useTableState } from '../../../hooks/useTableState';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { getPassivos } from '../../../services/passivosService';
import { Passivo, TIPO_LABEL } from '../../../types/passivos';
import TablePassivos from '../components/TablePassivos';
import CadastroAtivoForm from '../../../components/CadastroAtivoForm';
import SnackbarAlert from '../../../components/SnackbarAlert';

const PassivosPage: React.FC = () => {
  const [passivos, setPassivos]     = useState<Passivo[]>([]);
  const [pageCount, setPageCount]   = useState(0);
  const [loading, setLoading]       = useState(true);
  const [tipoFilter, setTipoFilter] = useState('');
  const [isCadastroModalOpen, setIsCadastroModalOpen] = useState(false);

  const { pagination, setPagination, sorting, setSorting, globalFilter, handleFilterChange, resetPage } = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const handleTipoFilterChange = (value: string) => {
    setTipoFilter(value);
    resetPage();
  };

  const fetchData = useCallback(async (
    pageIndex: number,
    pageSize: number,
    sortBy: typeof sorting,
    search: string,
    tipo: string,
  ) => {
    setLoading(true);
    try {
      const data = await getPassivos(pageIndex, pageSize, sortBy, search, tipo);
      setPassivos(data.results);
      setPageCount(data.totalPages);
    } catch {
      showError('Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter, tipoFilter);
  }, [fetchData, pagination.pageIndex, pagination.pageSize, sorting, globalFilter, tipoFilter]);

  const handleCadastroSuccess = () => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter, tipoFilter);
    showSuccess('Cadastro realizado com sucesso!');
    setIsCadastroModalOpen(false);
  };

  const columns = React.useMemo<ColumnDef<Passivo>[]>(() => [
    { header: 'Conta', accessorKey: 'conta' },
    { header: 'Nome',  accessorKey: 'descricao' },
    {
      header: 'Tipo',
      accessorKey: 'tipo',
      cell: ({ getValue }) => TIPO_LABEL[getValue<string>()] ?? getValue<string>(),
    },
  ], []);

  return (
    <div>
      <div className="page-header">
        <h2>Passivos</h2>
        <IconButton onClick={() => setIsCadastroModalOpen(true)}>
          <AddIcon />
        </IconButton>
      </div>

      <TablePassivos
        columns={columns}
        data={passivos}
        pageCount={pageCount}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={handleFilterChange}
        tipoFilter={tipoFilter}
        setTipoFilter={handleTipoFilterChange}
        isLoading={loading}
      />

      <Modal open={isCadastroModalOpen} onClose={() => setIsCadastroModalOpen(false)}>
        <div>
          <CadastroAtivoForm
            isOpen={isCadastroModalOpen}
            onRequestClose={() => setIsCadastroModalOpen(false)}
            onCadastroSuccess={handleCadastroSuccess}
            setSnackbarProps={() => {}}
          />
        </div>
      </Modal>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default PassivosPage;
